"use client";

/**
 * EntityCard - Reusable card for displaying candidates, contacts, and floats
 *
 * Provides a consistent card display for entity data with avatar,
 * name, title, tags, and optional click handling.
 */

import { Card, Avatar, Tag, Skeleton } from "@/ui";
import { Email } from "@mui/icons-material";
import { cn } from "@/lib/utils";
import { colors, spacing, radius, textStyles } from "@/ui/tokens";
import type { DisplayEntity } from "@/lib/sola/types/tools";
import cardStyles from "./EntityCard.module.css";

export interface EntityCardProps {
  entity: DisplayEntity;
  type: "candidate" | "contact" | "float";
  onClick?: () => void;
  compact?: boolean;
  className?: string;
  loading?: boolean;
}

/**
 * Loading skeleton for entity card
 */
function EntityCardSkeleton({ compact }: { compact?: boolean }) {
  return (
    <Card
      variant="outlined"
      padding={compact ? "sm" : "sm"}
      style={{ borderRadius: radius.md }}
    >
      <div style={{ display: "flex", gap: spacing[4], alignItems: "flex-start" }}>
        <Skeleton variant="circular" width={compact ? 36 : 48} height={compact ? 36 : 48} />
        <div style={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" />
          {!compact && <Skeleton variant="text" width="80%" />}
        </div>
      </div>
    </Card>
  );
}

export function EntityCard({
  entity,
  type,
  onClick,
  compact = false,
  className,
  loading = false,
}: EntityCardProps) {
  if (loading) {
    return <EntityCardSkeleton compact={compact} />;
  }

  const { name, title, email, avatar, status, tags } = entity;
  const displayName = name || title || `${type} #${entity.id}`;

  return (
    <Card
      variant="outlined"
      padding={compact ? "sm" : "sm"}
      className={cn(
        cardStyles.card,
        onClick && cardStyles.clickable,
        className
      )}
      onClick={onClick}
      style={{ borderRadius: radius.md }}
    >
      <div style={{ display: "flex", gap: spacing[4], alignItems: "flex-start" }}>
        {/* Avatar */}
        <Avatar
          src={avatar}
          name={name || displayName}
          size={compact ? "sm" : "md"}
        />

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name */}
          <span
            style={{
              ...(compact ? textStyles.body2 : textStyles.subtitle1),
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {displayName}
          </span>

          {/* Title/Role */}
          {title && name && (
            <span
              style={{
                ...textStyles.body2,
                color: colors.text.muted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                display: "block",
              }}
            >
              {title}
            </span>
          )}

          {/* Email (if not compact) */}
          {!compact && email && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacing[1],
                marginTop: spacing[1],
              }}
            >
              <Email style={{ fontSize: 14, color: colors.text.muted }} />
              <span
                style={{
                  ...textStyles.caption,
                  color: colors.text.muted,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {email}
              </span>
            </div>
          )}

          {/* Status badge */}
          {status && (
            <div style={{ marginTop: spacing[2] }}>
              <Tag
                variant="signal"
                size="sm"
                color={
                  status.toLowerCase().includes("available")
                    ? "success"
                    : status.toLowerCase().includes("error")
                    ? "danger"
                    : "neutral"
                }
              >
                {status}
              </Tag>
            </div>
          )}

          {/* Tags (if not compact and present) */}
          {!compact && tags && tags.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: spacing[1],
                marginTop: spacing[2],
              }}
            >
              {tags.slice(0, 4).map((tag, i) => (
                <Tag
                  key={i}
                  variant="default"
                  size="sm"
                >
                  {tag}
                </Tag>
              ))}
              {tags.length > 4 && (
                <Tag variant="minimal" size="sm">
                  +{tags.length - 4}
                </Tag>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default EntityCard;
